import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MessageSquare, User as UserIcon, CheckCircle2, Send, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Contact() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || params.get("name") || "",
    email: user?.email || params.get("email") || "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.message.trim().length < 10) {
      toast.error("El mensaje debe tener al menos 10 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("¡Mensaje enviado! Te responderemos lo antes posible.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo enviar el mensaje");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink relative overflow-hidden">
      <SEO
        title="Contacto · TestOpoGuardiaCivil"
        description="Contacta con el equipo de TestOpoGuardiaCivil. Te respondemos en menos de 24h."
      />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />

      <div className="relative">
        <Header />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gcgold/10 border border-gcgold/30 text-gcgold font-mono text-[11px] uppercase tracking-[0.22em] mb-5">
              <MessageSquare className="h-3 w-3" /> Contacto
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[0.95]">
              ¿Hablamos?
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-xl mx-auto">
              Escríbenos y te respondemos en menos de 24h. Dudas, sugerencias, incidencias, prensa o colaboraciones.
            </p>
          </motion.div>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-xl mx-auto text-center bg-ink-800 border border-emerald-500/30 rounded-2xl p-10"
              data-testid="contact-success"
            >
              <div className="h-16 w-16 rounded-2xl bg-emerald-500/15 mx-auto flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-extrabold text-white tracking-tight">¡Recibido!</h2>
              <p className="mt-2 text-slate-400">Tu mensaje está en nuestra bandeja. Te responderemos a <strong className="text-white">{form.email}</strong> lo antes posible.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/")} className="bg-gcgreen hover:bg-gcgreen-light text-white font-bold rounded-xl">
                  Volver al inicio
                </Button>
                <Button onClick={() => { setSent(false); setForm({ name: user?.name || "", email: user?.email || "", phone: "", message: "" }); }} variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
                  Enviar otro mensaje
                </Button>
              </div>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Info column */}
              <motion.aside
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-2 space-y-4"
              >
                <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gcgold/15 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-gcgold" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-white">Email</div>
                      <a href="mailto:hola@testopoguardiacivil.com" className="text-sm text-slate-400 hover:text-gcgold transition-colors block mt-0.5">
                        hola@testopoguardiacivil.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gcgreen/20 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-gcgreen-light" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-white">Chat IA</div>
                      <p className="text-sm text-slate-400 mt-0.5">¿Una duda rápida? Habla con TestOpoBot ahora mismo abajo a la derecha.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gcgreen/15 via-ink-800 to-ink-800 border border-gcgreen/30 rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gcgold font-mono font-bold">Respuesta</div>
                  <div className="font-display text-2xl font-extrabold text-white mt-1">&lt; 24h</div>
                  <p className="text-sm text-slate-400 mt-1.5">Lunes a viernes. Premium tiene soporte prioritario (&lt; 4h).</p>
                </div>
              </motion.aside>

              {/* Form column */}
              <motion.form
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                onSubmit={submit}
                className="lg:col-span-3 bg-ink-800 border border-white/5 rounded-2xl p-6 lg:p-8 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field icon={UserIcon} label="Nombre" required>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Tu nombre"
                      required
                      maxLength={80}
                      className="form-input"
                      data-testid="contact-name"
                    />
                  </Field>
                  <Field icon={Mail} label="Email" required>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="tu@email.com"
                      required
                      className="form-input"
                      data-testid="contact-email"
                    />
                  </Field>
                </div>

                <Field icon={Phone} label="Teléfono (opcional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+34 600 000 000"
                    maxLength={30}
                    className="form-input"
                    data-testid="contact-phone"
                  />
                </Field>

                <Field icon={MessageSquare} label="Mensaje" required>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                    required
                    minLength={10}
                    maxLength={3000}
                    rows={6}
                    className="form-input resize-none"
                    data-testid="contact-message"
                  />
                  <div className="text-[10px] font-mono text-slate-500 mt-1.5 text-right">
                    {form.message.length} / 3000
                  </div>
                </Field>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-gcgold hover:bg-gcgold-light text-ink font-bold h-12 px-6 rounded-xl flex-1 group"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Enviando..." : (<>Enviar mensaje <Send className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
                  </Button>
                  <Button asChild type="button" variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white h-12 rounded-xl">
                    <Link to="/"><ArrowLeft className="h-4 w-4 mr-1.5" /> Inicio</Link>
                  </Button>
                </div>

                <p className="text-[11px] text-slate-500 text-center">
                  Al enviar aceptas nuestra política de privacidad. Solo usamos tus datos para responderte.
                </p>
              </motion.form>
            </div>
          )}
        </main>

        <Footer />
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 12px 14px;
          color: white;
          font-size: 14px;
          transition: all 0.15s;
        }
        .form-input::placeholder { color: rgb(100, 116, 139); }
        .form-input:focus {
          outline: none;
          border-color: rgba(212, 175, 55, 0.45);
          background: rgba(255,255,255,0.06);
        }
      `}</style>
    </div>
  );
}

function Field({ icon: Icon, label, required, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3.5 w-3.5 text-gcgold" />
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-300 font-mono">
          {label}{required && <span className="text-rose-400 ml-1">*</span>}
        </span>
      </div>
      {children}
    </label>
  );
}
